import { useEffect, useState } from 'react'

export interface CarouselSlide {
  imageUrl: string
  alt: string
  href?: string
}

interface Props {
  slides: CarouselSlide[]
  intervalMs?: number
  aspectClassName?: string
}

/**
 * Auto-rotating hero image carousel. Site-agnostic — takes slide data as
 * props, no hardcoded content, so any per-bot site override can feed it
 * its own images/links.
 */
export function ImageCarousel({ slides, intervalMs = 4500, aspectClassName = 'h-48' }: Props) {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (slides.length <= 1) return
    const id = setInterval(() => setIndex((i) => (i + 1) % slides.length), intervalMs)
    return () => clearInterval(id)
  }, [slides.length, intervalMs])

  if (slides.length === 0) return null

  return (
    <div className={`relative w-full overflow-hidden ${aspectClassName}`}>
      {slides.map((slide, i) => {
        const img = (
          <img
            src={slide.imageUrl}
            alt={slide.alt}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${
              i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          />
        )
        return slide.href ? (
          <a key={slide.imageUrl} href={slide.href} target="_blank" rel="noreferrer" aria-hidden={i !== index} tabIndex={i === index ? undefined : -1} style={i === index ? undefined : { pointerEvents: 'none' }}>
            {img}
          </a>
        ) : (
          <div key={slide.imageUrl} aria-hidden={i !== index} tabIndex={i === index ? undefined : -1} style={i === index ? undefined : { pointerEvents: 'none' }}>
            {img}
          </div>
        )
      })}
      {slides.length > 1 && (
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
          {slides.map((slide, i) => (
            <button
              key={slide.imageUrl}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
